import { normalize, schema } from 'normalizr'
import {
  Id,
  IFindOrganizationTeamMembersResponseDTO,
  IOrganizationTeamMember,
  IOrganizationTeamMemberInvite,
  IOrganizationTeamMemberWithUserResponse,
  IStationUserInvite,
  IStateOrganizationTeamMember,
  StateOrganizationTeamMemberMap,
  IOrganizationTeamMemberUpdateDTO,
  IStationUserUpdateDTO,
  IMemberFilters,
  IMemberSearchObject,
  IStationUserSearchObject
} from '../types'
import Paths from '../utils/paths'
import { serverApi } from '../utils/request'

function serializeMember(member: IStateOrganizationTeamMember): IMemberSearchObject {
  const email = member.user.email ? member.user.email.emailAddress : ''
  const phoneObj = member.user.phones ? member.user.phones[0] : null
  const phone = phoneObj ? `${phoneObj.phoneNumber}` : ''
  const birthDate = member.user.birthDate ? new Date(member.user.birthDate).toISOString() : new Date().toISOString()
  return {
    id: member.id,
    userId: member.user.id,
    name: `${member.user.firstName} ${member.user.lastName}`,
    firstName: member.user.firstName,
    lastName: member.user.lastName,
    isArchived: member.isArchived,
    isActive: member.user.isActive,
    status: member.status,
    organizationId: member.organizationId,
    organizationName: member.organization.name,
    email,
    phone,
    countryCode: phoneObj.countryCode || '1',
    username: member.user.username,
    birthDate,
    roleIds: member.roles.map(r => (r as any).id || r),
    roles: member.roles.map(r => (r as any).roleName || r)
  }
}
function serializeStationUser(member: IStateOrganizationTeamMember): IStationUserSearchObject {
  const email = member.user.email ? member.user.email.emailAddress : ''
  const phoneObj = member.user.phones ? member.user.phones[0] : null
  const phone = phoneObj ? `${phoneObj.phoneNumber}` : ''
  const birthDate = member.user.birthDate ? new Date(member.user.birthDate).toISOString() : new Date().toISOString()
  return {
    id: member.id,
    userId: member.user.id,
    name: `${member.user.firstName} ${member.user.lastName}`,
    firstName: member.user.firstName,
    lastName: member.user.lastName,
    isArchived: member.isArchived,
    isActive: member.user.isActive,
    status: member.status,
    organizationId: member.organizationId,
    organizationName: member.organization.name,
    email,
    phone,
    countryCode: phoneObj.countryCode || '1',
    username: member.user.username,
    birthDate
  }
}

class TeamMemberService {
  public static usersSchema = new schema.Entity('users')
  public static rolesSchema = new schema.Entity('roles')
  public static organizationsSchema = new schema.Entity('organizations')
  public static teamMembers = new schema.Entity('teamMembers', {
    user: TeamMemberService.usersSchema,
    roles: [TeamMemberService.rolesSchema],
    organization: TeamMemberService.organizationsSchema
  })

  public async getMany(dto: IMemberFilters) {
    const resp: any = await serverApi.get(Paths.organizationTeamMember.search, {
      params: dto
    })
    return resp.data
  }
  public async fetchStationUserList(dto: IMemberFilters) {
    const resp: any = await serverApi.get(Paths.stationUser.search, {
      params: { search: dto.search, isArchived: dto.isArchived }
    })
    return resp.data
  }

  public async fetchTeamMembers(
    organizationId: string,
    statuses: string[],
    page?: number,
    recordsPerPage?: number
  ): Promise<IFindOrganizationTeamMembersResponseDTO> {
    try {
      const teamMembersResponse = await serverApi.get<any>(Paths.organizationTeamMember.base, {
        params: {
          statuses,
          organizationId,
          page,
          recordsPerPage
        }
      })
      const normalized = normalize(teamMembersResponse.data.items, [TeamMemberService.teamMembers])
      const { users, roles, organizations, teamMembers } = normalized.entities
      return {
        total: teamMembersResponse.data.total,
        users,
        roles,
        organizations,
        teamMembers
      }
    } catch (e) {
      throw e
    }
  }

  public async fetchTeamMember(id: Id): Promise<IMemberSearchObject> {
    const teamMember: any = await serverApi.get(`${Paths.organizationTeamMember.base}/${id}`)
    return serializeMember(teamMember.data as IStateOrganizationTeamMember)
  }

  public async fetchStationUser(id: Id): Promise<IStationUserSearchObject> {
    const teamMember: any = await serverApi.get(`${Paths.stationUser.base}/${id}`)
    return serializeStationUser(teamMember.data as IStateOrganizationTeamMember)
  }

  public searchTeamMembers(orgId: number, type: string, query: string): Promise<StateOrganizationTeamMemberMap> {
    // TODO: replace with a request
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        resolve({} as any)
      }, 1000)
    })
  }

  // Invite actually calls for createOne
  public async inviteTeamMember(teamMember: IOrganizationTeamMemberInvite): Promise<IMemberSearchObject> {
    try {
      const teamMembersResponse = await serverApi.post<IStateOrganizationTeamMember>(
        Paths.organizationTeamMember.createOne,
        teamMember
      )

      return serializeMember(teamMembersResponse.data)
    } catch (e) {
      throw e.data
    }
  }

  public async createStationUser(teamMember: IStationUserInvite): Promise<IStationUserSearchObject> {
    try {
      const teamMembersResponse = await serverApi.post<IStateOrganizationTeamMember>(
        Paths.stationUser.createStationUser,
        teamMember
      )
      return serializeStationUser(teamMembersResponse.data)
    } catch (e) {
      throw e.data
    }
  }

  public async updateOne(teamMember: IOrganizationTeamMemberUpdateDTO): Promise<any> {
    const resp = await serverApi.put<IStateOrganizationTeamMember>(Paths.organizationTeamMember.updateOne, teamMember)

    return serializeMember(resp.data)
  }

  public async updateOneStationUser(stationUser: IStationUserUpdateDTO): Promise<any> {
    const resp = await serverApi.put<IStateOrganizationTeamMember>(Paths.stationUser.update, stationUser)

    return serializeStationUser(resp.data)
  }

  public updateTeamMember(teamMember: IOrganizationTeamMember): Promise<IStateOrganizationTeamMember> {
    // TODO: replace with a request
    return new Promise((resolve, reject) => {
      setTimeout(() => resolve(teamMember as any), 1000)
    })
  }

  public moveToArchive(teamMember: IMemberSearchObject): Promise<any> {
    return serverApi.post(Paths.organizationTeamMember.archieve, {
      userId: teamMember.userId,
      organizationId: teamMember.organizationId,
      roles: []
    })
  }
  public moveToArchiveStationUser(teamMember: IStationUserSearchObject): Promise<any> {
    return serverApi.post(Paths.stationUser.archieve, {
      userId: teamMember.userId,
      organizationId: teamMember.organizationId
      // roles: []
    })
  }

  public restoreFromArchive(teamMember: IStationUserSearchObject): Promise<IStateOrganizationTeamMember> {
    // TODO: replace with a request
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        resolve(teamMember as any)
        // reject('error!!!')
      }, 1000)
    })
  }

  public sendResetPasswordLink(teamMember: IMemberSearchObject): Promise<any> {
    return serverApi.post(Paths.authentication.doSendPasswordRecoveryEmail, {
      email: teamMember.email
    })
  }

  public sendInvite(teamMember: IMemberSearchObject): Promise<any> {
    return serverApi.post(Paths.user.resendInvitation, {
      userId: teamMember.userId,
      organizationId: teamMember.organizationId
    })
  }
  public sendInviteStationUser(teamMember: IStationUserSearchObject): Promise<any> {
    return serverApi.post(Paths.user.resendInvitation, {
      userId: teamMember.userId,
      organizationId: teamMember.organizationId
    })
  }

  public acceptInvite(userId: string, organizationId: string): Promise<any> {
    return serverApi.post(
      Paths.organizationTeamMember.activate.replace(':userId', userId).replace(':organizationId', organizationId)
    )
  }
}

const teamMemberService = new TeamMemberService()

export { teamMemberService }



// WEBPACK FOOTER //
// ./src/services/OrganizationTeamMember.ts